import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { moduleService, sitesService, usersService, getErrorMessage } from '../services/api';
import {
  NativeButton,
  NativeCard,
  NativeCardHeader,
  NativeCardTitle,
  NativeCardContent,
  NativeAlert,
  EmptyState
} from '../components/NativeComponents';
import { 
  Loader2, 
  Settings,
  Building,
  Check,
  X,
  RefreshCw,
  Shield,
  CreditCard,
  BarChart3,
  Users,
  Zap,
  FileText,
  Database,
  DollarSign,
  Lock,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import UnauthorizedPage from './UnauthorizedPage';

// Modül bilgileri
const MODULES = [
  { key: 'news_center', label: 'Haber Merkezi', icon: FileText, description: 'Sektör haberleri, EV teknolojileri ve enerji içerikleri', default: true },
  { key: 'board', label: 'Pano', icon: MessageSquare, description: 'Kullanıcı geri bildirimleri, sorular ve öneriler', default: true },
  { key: 'rfid_management', label: 'RFID Kart Yönetimi', icon: CreditCard, description: 'RFID kartları görüntüleme ve yönetme' },
  { key: 'tariff_management', label: 'Tarife Yönetimi', icon: DollarSign, description: 'Şarj tarifelerini düzenleme' },
  { key: 'reports', label: 'Raporlar', icon: BarChart3, description: 'Detaylı raporlar ve istatistikler' },
  { key: 'user_management', label: 'Kullanıcı Yönetimi', icon: Users, description: 'Kullanıcı ekleme ve düzenleme' },
  { key: 'station_management', label: 'İstasyon Yönetimi', icon: Zap, description: 'Şarj istasyonları yönetimi' },
  { key: 'ocpp_dashboard', label: 'OCPP Dashboard', icon: Settings, description: 'OCPP cihaz izleme' },
  { key: 'audit_logs', label: 'Denetim Kayıtları', icon: FileText, description: 'Sistem aktivite logları' },
  { key: 'backups', label: 'Yedeklemeler', icon: Database, description: 'Veritabanı yedekleme' },
  { key: 'license_pricing', label: 'Lisans Fiyatlandırma', icon: DollarSign, description: 'Lisans ücretlerini belirleme' }
];

// Rol hiyerarşisi
const ROLE_HIERARCHY = {
  super_admin: ['admin'],
  admin: ['hesap_yoneticisi', 'site_manager'],
  hesap_yoneticisi: ['site_sakini', 'user'],
  site_manager: ['site_sakini', 'user']
};

// Rol etiketleri
const ROLE_LABELS = {
  super_admin: 'Süper Admin',
  admin: 'Admin',
  hesap_yoneticisi: 'Hesap Yöneticisi',
  site_manager: 'Hesap Yöneticisi',
  site_sakini: 'Site Sakini',
  user: 'Site Sakini'
};

export default function ModuleManagementPage() {
  const { user, canAccessModuleManagement, isSuperAdmin, isSuperOrAdmin, isHesapYoneticisi } = useAuth();
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [moduleConfigs, setModuleConfigs] = useState({});
  const [userPermissions, setUserPermissions] = useState({}); // Kullanıcı bazlı izinler
  const [currentUserPermissions, setCurrentUserPermissions] = useState({}); // Giriş yapan kullanıcının izinleri
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [error, setError] = useState(null);
  const [hasAccess] = useState(() => canAccessModuleManagement());
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRoles, setExpandedRoles] = useState({});
  const [viewMode, setViewMode] = useState('sites'); // 'sites' veya 'users'

  // Giriş yapan kullanıcının verebileceği roller
  const getAssignableRoles = useCallback(() => {
    if (!user) return [];
    return ROLE_HIERARCHY[user.role] || [];
  }, [user]);

  // Kullanıcının bir yetkiye sahip olup olmadığını kontrol et
  const hasPermission = useCallback((moduleKey) => {
    if (isSuperAdmin()) return true; // Süper Admin her şeye sahip
    return currentUserPermissions[moduleKey] === true;
  }, [currentUserPermissions, isSuperAdmin]);

  // Yetki verilebilir mi kontrol et
  const canGrantPermission = useCallback((moduleKey, targetRole) => {
    // Süper Admin her şeyi verebilir
    if (isSuperAdmin()) return true;
    
    // Kullanıcının kendisi bu yetkiye sahip mi?
    if (!hasPermission(moduleKey)) {
      return false;
    }
    
    // Hedef rol, kullanıcının verebileceği roller arasında mı?
    const assignableRoles = getAssignableRoles();
    return assignableRoles.includes(targetRole);
  }, [isSuperAdmin, hasPermission, getAssignableRoles]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Siteleri al
      const sitesData = await sitesService.list();
      setSites(sitesData || []);
      
      // Kullanıcıları al (yetki yönetimi için)
      try {
        const usersData = await usersService.list();
        setUsers(usersData || []);
      } catch (err) {
        console.error('Kullanıcılar alınamadı:', err);
      }
      
      // Her site için modül konfigürasyonunu al
      const configs = {};
      for (const site of sitesData || []) {
        try {
          const config = await moduleService.get(site.id);
          configs[site.id] = config;
        } catch (err) {
          configs[site.id] = {
            rfid_management: true,
            tariff_management: true,
            reports: true,
            user_management: true,
            station_management: true,
            ocpp_dashboard: true,
            audit_logs: false,
            backups: false,
            license_pricing: false,
            news_center: true,
            board: true
          };
        }
      }
      setModuleConfigs(configs);
      
      // Giriş yapan kullanıcının izinlerini al
      if (user && !isSuperAdmin()) {
        try {
          const userPerms = await moduleService.getUserPermissions(user.id);
          setCurrentUserPermissions(userPerms || {});
        } catch (err) {
          // Varsayılan izinler
          if (user.role === 'admin') {
            setCurrentUserPermissions({
              rfid_management: true,
              tariff_management: true,
              reports: true,
              user_management: true,
              station_management: true,
              ocpp_dashboard: true,
              audit_logs: true,
              backups: true,
              license_pricing: true
            });
          }
        }
      }
      
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [user, isSuperAdmin]);

  useEffect(() => {
    if (hasAccess) {
      fetchData();
    }
  }, [fetchData, hasAccess]);

  if (!hasAccess) {
    return <UnauthorizedPage message="Modül yönetimi sayfasına erişim yetkiniz bulunmamaktadır." />;
  }

  const toggleModule = async (siteId, moduleKey, currentValue) => {
    console.log(`Modül güncelleniyor: ${moduleKey}, mevcut durum: ${currentValue}, yeni durum: ${!currentValue}`);
    
    // Yetki kontrolü
    if (!isSuperAdmin() && !hasPermission(moduleKey)) {
      toast.error(`Bu yetkiyi vermek için önce size "${MODULES.find(m => m.key === moduleKey)?.label}" yetkisi verilmeli.`);
      return;
    }
    
    const savingKey = `${siteId}-${moduleKey}`;
    setSaving(prev => ({ ...prev, [savingKey]: true }));
    
    try {
      console.log(`API çağrısı yapılıyor: PUT /api/modules/${siteId}`, { [moduleKey]: !currentValue });
      await moduleService.update(siteId, { [moduleKey]: !currentValue });
      console.log('API çağrısı başarılı');
      
      setModuleConfigs(prev => ({
        ...prev,
        [siteId]: {
          ...prev[siteId],
          [moduleKey]: !currentValue
        }
      }));
      
      toast.success(`${MODULES.find(m => m.key === moduleKey)?.label} ${!currentValue ? 'açıldı' : 'kapatıldı'}`);
    } catch (err) {
      console.error('API hatası:', err);
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(prev => ({ ...prev, [savingKey]: false }));
    }
  };

  // Kullanıcı bazlı yetki toggle
  const toggleUserPermission = async (userId, moduleKey, currentValue) => {
    const targetUser = users.find(u => u.id === userId);
    if (!targetUser) return;
    
    // Yetki zinciri kontrolü
    if (!canGrantPermission(moduleKey, targetUser.role)) {
      if (!hasPermission(moduleKey)) {
        toast.error(`Bu yetkiyi veremezsiniz çünkü kendiniz "${MODULES.find(m => m.key === moduleKey)?.label}" yetkisine sahip değilsiniz.`, { duration: 5000 });
      } else {
        toast.error(`${ROLE_LABELS[targetUser.role]} rolüne yetki verme izniniz yok.`);
      }
      return;
    }
    
    const savingKey = `user-${userId}-${moduleKey}`;
    setSaving(prev => ({ ...prev, [savingKey]: true }));
    
    try {
      await moduleService.updateUserPermission(userId, { [moduleKey]: !currentValue });
      
      setUserPermissions(prev => ({
        ...prev,
        [userId]: {
          ...prev[userId],
          [moduleKey]: !currentValue
        }
      }));
      
      toast.success(`${targetUser.full_name || targetUser.email} için ${MODULES.find(m => m.key === moduleKey)?.label} ${!currentValue ? 'açıldı' : 'kapatıldı'}`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(prev => ({ ...prev, [savingKey]: false }));
    }
  };

  // Rol bazlı kullanıcıları grupla
  const getUsersByRole = () => {
    const assignableRoles = getAssignableRoles();
    const grouped = {};
    
    assignableRoles.forEach(role => {
      grouped[role] = users.filter(u => u.role === role);
    });
    
    return grouped;
  };

  const toggleRoleExpand = (role) => {
    setExpandedRoles(prev => ({ ...prev, [role]: !prev[role] }));
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const usersByRole = getUsersByRole();
  const assignableRoles = getAssignableRoles();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} data-testid="module-management-page">
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ margin: 0 }}>Modül Yönetimi</h1>
          <p className="text-slate-500 dark:text-slate-400" style={{ margin: '4px 0 0' }}>
            {isSuperAdmin() ? 'Admin rollerine yetki verin' : 
             isSuperOrAdmin() ? 'Hesap yöneticilerine yetki verin (sadece size verilmiş yetkiler kadar)' :
             'Site sakinlerine yetki verin (sadece size verilmiş yetkiler kadar)'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {/* Görünüm Modu */}
          <div style={{ display: 'flex', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            <button
              onClick={() => setViewMode('sites')}
              style={{
                padding: '8px 16px',
                border: 'none',
                backgroundColor: viewMode === 'sites' ? '#3b82f6' : 'white',
                color: viewMode === 'sites' ? 'white' : '#64748b',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Hesap Bazlı
            </button>
            <button
              onClick={() => setViewMode('users')}
              style={{
                padding: '8px 16px',
                border: 'none',
                backgroundColor: viewMode === 'users' ? '#3b82f6' : 'white',
                color: viewMode === 'users' ? 'white' : '#64748b',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Kullanıcı Bazlı
            </button>
          </div>
          
          {viewMode === 'sites' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <input
                type="text"
                placeholder="Hesap ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  backgroundColor: 'white',
                  fontSize: '14px',
                  minWidth: '180px'
                }}
                className="dark:bg-slate-800 dark:border-slate-600 dark:text-white"
              />
              <select
                value={selectedSiteId}
                onChange={(e) => setSelectedSiteId(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  backgroundColor: 'white',
                  fontSize: '14px',
                  minWidth: '200px',
                  cursor: 'pointer'
                }}
                className="dark:bg-slate-800 dark:border-slate-600 dark:text-white"
              >
                <option value="">Tüm Hesaplar</option>
                {sites
                  .filter(site => site.name.toLowerCase().includes(searchTerm.toLowerCase()))
                  .map(site => (
                    <option key={site.id} value={site.id}>{site.name}</option>
                  ))}
              </select>
            </div>
          )}
          
          <NativeButton variant="outline" onClick={fetchData}>
            <RefreshCw style={{ width: '16px', height: '16px' }} />
            Yenile
          </NativeButton>
        </div>
      </div>

      {error && (
        <NativeAlert variant="danger">
          {error}
        </NativeAlert>
      )}

      {/* Yetki Zinciri Bilgilendirme */}
      <NativeAlert variant="info">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <Shield style={{ width: '20px', height: '20px', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <div style={{ fontWeight: '600', marginBottom: '4px' }}>Yetki Zinciri Kuralları</div>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#475569' }}>
              <li><strong>Süper Admin</strong> → Admin'e tüm yetkileri verebilir</li>
              <li><strong>Admin</strong> → Hesap Yöneticisi'ne yetki verebilir (sadece kendisine verilmiş yetkiler kadar)</li>
              <li><strong>Hesap Yöneticisi</strong> → Site Sakini'ne yetki verebilir (sadece kendisine verilmiş yetkiler kadar)</li>
            </ul>
            {!isSuperAdmin() && (
              <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#fef3c7', borderRadius: '6px', fontSize: '13px' }}>
                <AlertTriangle style={{ width: '14px', height: '14px', display: 'inline', marginRight: '6px', color: '#d97706' }} />
                Sadece size verilmiş yetkileri alt kullanıcılara verebilirsiniz.
              </div>
            )}
          </div>
        </div>
      </NativeAlert>

      {/* Hesap Bazlı Görünüm */}
      {viewMode === 'sites' && (
        <>
          {sites.length === 0 ? (
            <EmptyState
              icon={Building}
              title="Site bulunamadı"
              description="Henüz site eklenmemiş"
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {sites
                .filter(site => !selectedSiteId || site.id === selectedSiteId)
                .filter(site => !searchTerm || site.name.toLowerCase().includes(searchTerm.toLowerCase()))
                .map(site => (
                <NativeCard key={site.id}>
                  <NativeCardHeader>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ 
                        width: '40px', 
                        height: '40px', 
                        borderRadius: '8px', 
                        backgroundColor: '#dbeafe',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Building style={{ width: '20px', height: '20px', color: '#2563eb' }} />
                      </div>
                      <div>
                        <NativeCardTitle>{site.name}</NativeCardTitle>
                        <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>{site.address || 'Adres belirtilmemiş'}</p>
                      </div>
                    </div>
                  </NativeCardHeader>
                  <NativeCardContent style={{ padding: 0 }}>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
                      gap: '1px',
                      backgroundColor: '#e5e7eb'
                    }} className="dark:bg-slate-700">
                      {MODULES.map(module => {
                        const isEnabled = moduleConfigs[site.id]?.[module.key] ?? true;
                        const savingKey = `${site.id}-${module.key}`;
                        const isSaving = saving[savingKey];
                        const Icon = module.icon;
                        const canToggle = isSuperAdmin() || hasPermission(module.key);
                        
                        return (
                          <div 
                            key={module.key}
                            style={{ 
                              padding: '16px',
                              backgroundColor: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '12px',
                              opacity: canToggle ? 1 : 0.6
                            }}
                            className="dark:bg-slate-800"
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                              <div style={{ 
                                width: '36px', 
                                height: '36px', 
                                borderRadius: '8px', 
                                backgroundColor: isEnabled ? '#ecfdf5' : '#f1f5f9',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <Icon style={{ 
                                  width: '18px', 
                                  height: '18px', 
                                  color: isEnabled ? '#16a34a' : '#94a3b8' 
                                }} />
                              </div>
                              <div>
                                <div style={{ fontWeight: '500', fontSize: '14px' }} className="text-slate-900 dark:text-white">
                                  {module.label}
                                  {!canToggle && (
                                    <Lock style={{ width: '12px', height: '12px', display: 'inline', marginLeft: '6px', color: '#f59e0b' }} />
                                  )}
                                </div>
                                <div style={{ fontSize: '12px', color: '#64748b' }}>
                                  {module.description}
                                </div>
                              </div>
                            </div>
                            
                            <button
                              onClick={() => toggleModule(site.id, module.key, isEnabled)}
                              disabled={isSaving || !canToggle}
                              title={!canToggle ? 'Bu yetkiye sahip değilsiniz' : ''}
                              style={{
                                width: '48px',
                                height: '28px',
                                borderRadius: '14px',
                                border: 'none',
                                backgroundColor: !canToggle ? '#94a3b8' : isEnabled ? '#16a34a' : '#d1d5db',
                                cursor: isSaving || !canToggle ? 'not-allowed' : 'pointer',
                                position: 'relative',
                                transition: 'background-color 0.2s',
                                opacity: isSaving ? 0.7 : 1
                              }}
                            >
                              <div style={{
                                width: '22px',
                                height: '22px',
                                borderRadius: '50%',
                                backgroundColor: 'white',
                                position: 'absolute',
                                top: '3px',
                                left: isEnabled ? '23px' : '3px',
                                transition: 'left 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                              }}>
                                {isSaving ? (
                                  <Loader2 className="animate-spin" style={{ width: '12px', height: '12px', color: '#94a3b8' }} />
                                ) : !canToggle ? (
                                  <Lock style={{ width: '12px', height: '12px', color: '#94a3b8' }} />
                                ) : isEnabled ? (
                                  <Check style={{ width: '12px', height: '12px', color: '#16a34a' }} />
                                ) : (
                                  <X style={{ width: '12px', height: '12px', color: '#94a3b8' }} />
                                )}
                              </div>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </NativeCardContent>
                </NativeCard>
              ))}
            </div>
          )}
        </>
      )}

      {/* Kullanıcı Bazlı Görünüm */}
      {viewMode === 'users' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {assignableRoles.length === 0 ? (
            <EmptyState
              icon={Users}
              title="Yetki verebileceğiniz rol yok"
              description="Üst yöneticinizden yetki talep edin"
            />
          ) : (
            assignableRoles.map(role => {
              const roleUsers = usersByRole[role] || [];
              const isExpanded = expandedRoles[role] !== false;
              
              return (
                <NativeCard key={role}>
                  <NativeCardHeader>
                    <button
                      onClick={() => toggleRoleExpand(role)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        width: '100%',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0
                      }}
                    >
                      {isExpanded ? <ChevronDown style={{ width: '20px', height: '20px' }} /> : <ChevronRight style={{ width: '20px', height: '20px' }} />}
                      <div style={{ 
                        width: '40px', 
                        height: '40px', 
                        borderRadius: '8px', 
                        backgroundColor: '#fef3c7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Users style={{ width: '20px', height: '20px', color: '#d97706' }} />
                      </div>
                      <div style={{ textAlign: 'left' }}>
                        <NativeCardTitle>{ROLE_LABELS[role]}</NativeCardTitle>
                        <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>{roleUsers.length} kullanıcı</p>
                      </div>
                    </button>
                  </NativeCardHeader>
                  
                  {isExpanded && (
                    <NativeCardContent>
                      {roleUsers.length === 0 ? (
                        <p style={{ color: '#64748b', textAlign: 'center', padding: '20px' }}>
                          Bu rolde kullanıcı bulunmuyor
                        </p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {roleUsers.map(targetUser => (
                            <div key={targetUser.id} style={{ 
                              padding: '16px', 
                              backgroundColor: '#f8fafc', 
                              borderRadius: '12px',
                              border: '1px solid #e2e8f0'
                            }} className="dark:bg-slate-700 dark:border-slate-600">
                              <div style={{ marginBottom: '12px', fontWeight: '600' }} className="text-slate-900 dark:text-white">
                                {targetUser.full_name || targetUser.email}
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {MODULES.map(module => {
                                  const userPerm = userPermissions[targetUser.id]?.[module.key];
                                  const isEnabled = userPerm ?? false;
                                  const savingKey = `user-${targetUser.id}-${module.key}`;
                                  const isSaving = saving[savingKey];
                                  const canToggle = canGrantPermission(module.key, targetUser.role);
                                  
                                  return (
                                    <button
                                      key={module.key}
                                      onClick={() => canToggle && toggleUserPermission(targetUser.id, module.key, isEnabled)}
                                      disabled={isSaving || !canToggle}
                                      title={!canToggle ? 'Bu yetkiyi vermek için önce size bu yetki verilmeli' : module.description}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '6px 12px',
                                        borderRadius: '20px',
                                        border: '1px solid',
                                        borderColor: !canToggle ? '#cbd5e1' : isEnabled ? '#16a34a' : '#e2e8f0',
                                        backgroundColor: !canToggle ? '#f1f5f9' : isEnabled ? '#dcfce7' : 'white',
                                        color: !canToggle ? '#94a3b8' : isEnabled ? '#16a34a' : '#64748b',
                                        cursor: isSaving || !canToggle ? 'not-allowed' : 'pointer',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        opacity: isSaving ? 0.7 : 1
                                      }}
                                    >
                                      {isSaving ? (
                                        <Loader2 className="animate-spin" style={{ width: '12px', height: '12px' }} />
                                      ) : !canToggle ? (
                                        <Lock style={{ width: '12px', height: '12px' }} />
                                      ) : isEnabled ? (
                                        <Check style={{ width: '12px', height: '12px' }} />
                                      ) : (
                                        <X style={{ width: '12px', height: '12px' }} />
                                      )}
                                      {module.label}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </NativeCardContent>
                  )}
                </NativeCard>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
